import { z } from "zod";

import { locationIdentitySchema } from "@/shared/models/location";
import { validateObject, ObjectErrors } from "@/src/lib/utils/object-utils";
import { Address, addressSchema, addressShape, getFormattedAddress } from "@/src/models/address";
import { phoneNumberSchema } from "@/src/models/common-schema";
import { FACILITY_TYPE_PROMPT, FacilityType } from "@/src/models/constants/facility-types";
import { DatabaseDefaults } from "@/src/models/database-defaults";

export { FacilityType };

export enum LocationRole {
  RECEIVING_LOCATION = "Receiving Location",
  REFERRING_LOCATION = "Referring Location",
}

export const locationSchema = z.object({
  facilityType: z.nativeEnum(FacilityType).nullable().describe(FACILITY_TYPE_PROMPT),
  name: locationIdentitySchema.shape.name,
  faxPhoneNumber: phoneNumberSchema.nullable().describe(
    `
    This is not any phone number that is associated with the location, but rather the fax phone number of the location.
    Look for the word "Fax" or "Fax Number" to find the fax phone number.
    `
  ),
  phoneNumber: phoneNumberSchema.nullable(),
  address: addressSchema.nullable(),
  role: z
    .nativeEnum(LocationRole)
    .describe(
      "The role of the location in the referral typically. A receiving location is the location where the patient will be treated. A referring location is the location where the patient was referred from."
    ),
});

export type Location = Omit<z.infer<typeof locationSchema>, "address"> & {
  address: Address | null;
  alternativeFaxPhoneNumbers: string[] | null;
  alternativePhoneNumbers: string[] | null;
  placeId: string | null;
  ehrId: string | null;
  displayId: string | null;
  isApproved: boolean | null;
  isDuplicate: boolean | null;
  metadata: Record<string, string> | null;
} & DatabaseDefaults;

export const locationShape: Location = {
  id: "",
  groupId: "",
  facilityType: null,
  name: null,
  faxPhoneNumber: null,
  alternativeFaxPhoneNumbers: null,
  phoneNumber: null,
  alternativePhoneNumbers: null,
  address: addressShape,
  createdAt: "",
  updatedAt: "",
  deletedAt: null,
  role: LocationRole.REFERRING_LOCATION,
  placeId: null,
  ehrId: null,
  displayId: null,
  isApproved: null,
  isDuplicate: null,
  metadata: null,
};

export const isEmptyLocation = (location: Location | null | undefined) => {
  const address = location?.address;
  const hasAddress =
    !!address &&
    !!(address.line1 || address.line2 || address.city || address.state || address.postalCode || address.district);

  return !location || (!location.name && !location.phoneNumber && !location.faxPhoneNumber && !hasAddress);
};

export const getLocationDisplayName = (location: Location): string => {
  const name = location.name?.trim();
  const address = getFormattedAddress(location.address);

  if (name && address) {
    return `${name} (${address})`;
  }

  return name || address || "None";
};

export type LocationErrors = ObjectErrors<Location>;
export type LocationFieldKey = keyof Location | `address.${keyof Address}`;

export const LocationMetadataKeys = {
  SCHEDULING_DISPLAY_NAME: "schedulingDisplayName",
  HEALOW_FACILITY_ID: "healowFacilityId",
} as const;

export function getLocationMetadata(location: Location, key: string): string | null {
  return location.metadata?.[key] ?? null;
}

export function setLocationMetadata(location: Location, key: string, value: string | null): Location {
  const newMetadata = { ...(location.metadata ?? {}) };

  if (value === null) {
    delete newMetadata[key];
  } else {
    newMetadata[key] = value;
  }

  return {
    ...location,
    metadata: Object.keys(newMetadata).length > 0 ? newMetadata : null,
  };
}

export function getLocationSchedulingDisplayName(location: Location): string {
  const schedulingDisplayName = getLocationMetadata(location, LocationMetadataKeys.SCHEDULING_DISPLAY_NAME);

  return schedulingDisplayName ?? location.name ?? "N/A";
}

export function getLocationHealowFacilityId(location: Location): string | null {
  return getLocationMetadata(location, LocationMetadataKeys.HEALOW_FACILITY_ID);
}

/**
 * Validates a Location object using the generic validateObject helper.
 * If fieldsToValidate is provided, only those fields are validated. Supports nested address fields as 'address.field'.
 */
export function validateLocation(location: Location, fieldsToValidate?: LocationFieldKey[]) {
  return validateObject<Location>(locationSchema, location, fieldsToValidate);
}
